import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { isPasswordValid, removeWhitespacesInIban } from '@mng/mng-ts-helpers';
import { validateBIC, validateIBAN } from 'ibantools';
import pL from 'js-regex-pl';

export function loginNameCharValidator(control: FormControl<string>): ValidationErrors | null {
  const regex = /^[a-zA-Z0-9.\-@]+$/;
  return regex.test(control.value) ? null : { forbiddenChar: true };
}

export function merlinForbiddenSpecialCharsValidator(control: FormControl): { [key: string]: any } {
  const regex = /^[^<>;"|\[\]*~^]+$/;
  const valid = !control.value || regex.test(control.value);
  return valid ? null : { 'Folgende Zeichen sind nicht erlaubt: <>;"|[]*~^': true };
}

export function warenkorbForbiddenSpecialCharsValidator(control: FormControl<string>): ValidationErrors | null {
  const regex = /^[^~<>;"|?\\*']*$/;
  return regex.test(control.value) ? null : { forbiddenEsignSpecialChar: true };
}

export function merlinNaturalPersonNameSymbolsValidator(control: FormControl<string>): ValidationErrors | null {
  const regex = new RegExp(`^[${pL}].*[${pL}]$`);
  return regex.test(control.value) ? null : { invalidNameFormat: true };
}

export function loginNameNotOnlyNumbersValidator(control: FormControl<string>): ValidationErrors | null {
  const regex = /^[0-9]+$/;
  return !regex.test(control.value) ? null : { notOnlyNumbers: true };
}

export function passwordValidator(control: FormControl<string>): ValidationErrors | null {
  const valid = !control.value || isPasswordValid(control.value);
  return valid ? null : { password: true };
}

export function ibanValidator(errorMessage?: string, ignoreCase = false): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    const ibanWithoutSpaces: string = removeWhitespacesInIban(control.value);
    const validationResult = validateIBAN(ignoreCase ? ibanWithoutSpaces.toUpperCase() : ibanWithoutSpaces);

    const valid = validationResult.valid;

    if (valid) {
      return null;
    }

    return errorMessage ? { errorMessage: errorMessage } : { iban: { value: control.value } };
  };
}

export function bicValidator(errorMessage?: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    const bic: string = control.value;

    if (validateBIC(bic).valid) {
      return null;
    }

    return errorMessage ? { errorMessage: errorMessage } : { bic: { value: control.value } };
  };
}

export const onlyNumbersValidator = (control: FormControl<string>): ValidationErrors | null => {
  const regex = /^\d*$/;
  const valid = !control.value || regex.test(control.value);
  return valid ? null : { onlyNumbers: true };
};

export const twoDecimalPlaceNumberValidator = (control: FormControl<string>): ValidationErrors | null => {
  // only allow this format with max 2 decimal places: "1234,56" or "1234.56"
  const regex = /^-?\d+([,.]\d{1,2})?$/;
  const valid = !control.value || regex.test(control.value);
  return valid ? null : { onlyDecimalNumbersWith2Places: true };
};

export const singleDecimalPlaceNumberValidator = (control: FormControl<string>): ValidationErrors | null => {
  // only allow this format with max 1 decimal place: "1234,5" or "1234.7"
  const regex = /^-?\d+([,.]\d)?$/;
  const valid = !control.value || regex.test(control.value);
  return valid ? null : { onlyDecimalNumbersWith1Place: true };
};

export const prozentValidator = (control: FormControl<string>): ValidationErrors | null => {
  if (!control.value) {
    return null;
  }
  const value = Number(control.value.replaceAll(',', '.'));
  if (isNaN(value) || value < 0 || value > 100) {
    return { prozent: true };
  }
  return null;
};

export function exactLengthValidator(length: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const strValue = control.value.toString();
    return strValue.length !== length
      ? { exactLength: { requiredLength: length, actualLength: strValue.length } }
      : null;
  };
}

/**
 * Erzeugt einen FormGroup Validator welcher die Gleichheit zweier Controls überprüft
 *
 * Verwendet die ValidationError ID 'match'
 */
export function createMatchValidator(control1Name: string, control2Name: string) {
  const matchValidator = (formGroup: FormGroup): ValidationErrors | null => {
    const control1 = formGroup.get(control1Name);
    const control2 = formGroup.get(control2Name);

    if (!control1 || !control2) {
      return null;
    }

    const value1 = control1.value;
    const value2 = control2.value;
    if (!value1 || !value2) {
      return null;
    }

    if (value1 === value2) {
      const nErrors1 = { ...control1.errors };
      delete nErrors1.match;
      if (Object.keys(nErrors1).length === 0) {
        control1.setErrors(null);
      } else {
        control1.setErrors(nErrors1);
      }

      const nErrors2 = { ...control2.errors };
      delete nErrors2.match;
      if (Object.keys(nErrors2).length === 0) {
        control2.setErrors(null);
      } else {
        control2.setErrors(nErrors2);
      }

      return null;
    }

    const newErrors1 = { ...control1.errors, match: true };
    control1.setErrors(newErrors1);

    const newErrors2 = { ...control2.errors, match: true };
    control2.setErrors(newErrors2);

    return { match: true };
  };

  return matchValidator;
}

/**
 * Erzeugt einen FormGroup Validator welcher SVNR und SV Gebdatum auf Gültigkeit prüft
 *
 * Verwendet die ValidationError ID 'svnr'
 */
export function createSvnrValidator(control1Name: string, control2Name: string) {
  const svnrValidator = (formGroup: FormGroup): ValidationErrors | null => {
    const control1 = formGroup.get(control1Name);
    const control2 = formGroup.get(control2Name);

    if (!control1 || !control2) {
      return null;
    }

    const versNr = control1.value;
    const gebdatum = control2.value;
    if (!versNr || !gebdatum) {
      return null;
    }

    let valid = true;
    if (gebdatum && gebdatum.length === 6) {
      const va = versNr.toString().split('').map(Number);
      const ga = gebdatum.split('').map(Number);
      const pruefziffer = va[3];

      const calculatedPruefziffer =
        (va[0] * 3 + va[1] * 7 + va[2] * 9 + ga[0] * 5 + ga[1] * 8 + ga[2] * 4 + ga[3] * 2 + ga[4] * 1 + ga[5] * 6) %
        11;
      if (pruefziffer !== calculatedPruefziffer) {
        valid = false;
      }
    } else if (!gebdatum) {
      valid = false;
    }

    if (valid) {
      const nErrors1 = { ...control1.errors };
      delete nErrors1.svnr;
      if (Object.keys(nErrors1).length === 0) {
        control1.setErrors(null);
      } else {
        control1.setErrors(nErrors1);
      }

      const nErrors2 = { ...control2.errors };
      delete nErrors2.svnr;
      if (Object.keys(nErrors2).length === 0) {
        control2.setErrors(null);
      } else {
        control2.setErrors(nErrors2);
      }

      return null;
    }

    const newErrors1 = { ...control1.errors, svnr: true };
    control1.setErrors(newErrors1);

    const newErrors2 = { ...control2.errors, svnr: true };
    control2.setErrors(newErrors2);

    return { svnr: true };
  };

  return svnrValidator;
}
